/** Which tiles Platform integrations shows and which connections are a person's own, shared by the web and native so they cannot drift. Personal connections live in You > Connected accounts for everyone; Platform integrations is the platform's own keys, for super admins only. */

/** Connections a person makes for their own account. */
export const PERSONAL_SYSTEM_PLUGIN_IDS: readonly string[] = ['google-workspace', 'microsoft-365', 'gmail-addon', 'push-notifications'];

export const isPersonalConnection = (id: string): boolean => PERSONAL_SYSTEM_PLUGIN_IDS.includes(id.replace(/^(?:integrations|connections)\//, ''));

/** Where an old address of a personal connection opens now (connections/<id>), from a
 *  Platform integrations tab, a Workspace > Integrations tab or the old Account Gmail
 *  page; null for anything else. */
export function personalConnectionTab(tab: string | null | undefined): string | null {
  const id = tab === 'gmail-addon' ? tab : tab?.match(/^(?:workspace\/)?integrations\/([a-z0-9-]+)$/)?.[1];
  return id && PERSONAL_SYSTEM_PLUGIN_IDS.includes(id) ? `connections/${id}` : null;
}

/** The roles that have System Settings at all. */
export const SYSTEM_SETTINGS_ROLES: readonly string[] = ['super_admin', 'internal', 'admin', 'staff'];

// A customer's account-holder carries the role 'admin'; their company's Xero and Stripe live under Workspace > Integrations, not here.
export function systemPluginTileVisible(id: string, role: string | null | undefined): boolean {
  if (isPersonalConnection(id)) return false;
  return role === 'super_admin';
}

/** Every plugin's group, name and one-line description, as the web's tile grid shows them; the personal ones are drawn in Connected accounts. */
export const SYSTEM_PLUGIN_GROUPS: ReadonlyArray<{ label: string; plugins: ReadonlyArray<{ id: string; name: string; description: string }> }> = [
  { label: 'Accounting & Payments', plugins: [
    { id: 'xero', name: 'Xero', description: 'Accounting & invoices' },
    { id: 'stripe', name: 'Stripe', description: 'Payments & subscriptions' },
  ] },
  { label: 'Communication', plugins: [
    { id: 'google-workspace', name: 'Google Workspace', description: 'Calendar, Drive & Gmail' },
    { id: 'microsoft-365', name: 'Microsoft 365', description: 'Your Outlook mailbox' },
    { id: 'gmail-addon', name: 'Gmail add-on', description: 'Link emails from Gmail' },
    { id: 'smtp', name: 'Email (SMTP)', description: 'Outbound email delivery' },
    { id: 'shared-inboxes', name: 'Shared Inboxes', description: 'Group email registry & team inbox routing' },
  ] },
  { label: 'Location & Data Intelligence', plugins: [
    { id: 'google-maps', name: 'Google Maps', description: 'Address & geocoding' },
    { id: 'companies-house', name: 'Companies House', description: 'UK company search' },
    { id: 'creditsafe', name: 'Creditsafe', description: 'Credit & financial data' },
    { id: 'ons', name: 'ONS', description: 'UK market statistics' },
  ] },
  { label: 'Notifications & Mobile', plugins: [
    { id: 'push-notifications', name: 'Push Notifications', description: 'APNs & web push' },
    { id: 'apple-wallet', name: 'Apple Wallet', description: 'Digital passes & cards' },
  ] },
  { label: 'Infrastructure', plugins: [
    { id: 'supabase', name: 'Supabase', description: 'Database, auth & backend' },
    { id: 'cloud-build', name: 'Cloud Build', description: 'Edge function deploys' },
  ] },
];

/** You > Connected accounts, in the order both platforms list them. */
export const PERSONAL_CONNECTIONS = PERSONAL_SYSTEM_PLUGIN_IDS.map(id => SYSTEM_PLUGIN_GROUPS.flatMap(group => group.plugins).find(plugin => plugin.id === id)!);
